import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowCounterClockwise, ShieldCheck, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { jstDate } from '@/utils/jstDate';
import DataQualityAudit from '@/pages/settings/DataQualityAudit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shizai/purchase-records`;
const yen = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? '—' : `¥${Number(n).toLocaleString('ja-JP', { maximumFractionDigits: 2 })}`);

const Section = ({ title, count, note, children, testid }) => (
  <section className="rounded-xl border border-zinc-200 bg-white" data-testid={testid}>
    <header className="flex items-baseline gap-2 border-b border-zinc-100 px-3 py-2">
      <h2 className="text-[13px] font-semibold text-zinc-800">{title}</h2>
      <span className={`text-sm font-bold ${count ? 'text-amber-700' : 'text-zinc-300'}`}>{count}</span>
      {note && <span className="text-[11px] text-zinc-400">{note}</span>}
    </header>
    <div className="divide-y divide-zinc-100">{children}</div>
  </section>
);

/** 購入記録の点検（ドライラン）。適用は1グループずつ・取り消しは元に戻せる。 */
export default function PurchaseRecordAudit() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [undoable, setUndoable] = useState([]);
  const [stockQty, setStockQty] = useState({});
  const [showReflected, setShowReflected] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/audit`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '点検できませんでした');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (body, label) => {
    if (!window.confirm(`${label}\nこの1件だけを直します。よろしいですか？`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/audit/apply`, body);
      setUndoable((u) => [{ id: r.data.repair_batch_id, label }, ...u].slice(0, 20));
      toast.success('直しました（元に戻せます）');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '適用できませんでした');
    } finally { setBusy(false); }
  };

  const undo = async (batch) => {
    setBusy(true);
    try {
      await axios.post(`${API}/audit/undo/${batch.id}`);
      setUndoable((u) => u.filter((x) => x.id !== batch.id));
      toast.success('元に戻しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '戻せませんでした');
    } finally { setBusy(false); }
  };

  if (!data) return <p className="p-6 text-sm text-zinc-400" data-testid="pr-audit-loading">読み込み中…</p>;
  const c = data.counts;

  return (
    <div className="space-y-3 pb-24" data-testid="purchase-record-audit">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900"
          data-testid="data-audit-title">データ点検</h1>
        <p className="mt-0.5 text-xs text-zinc-500">
          仮データ・不整合と購入記録を点検します。どちらも開いただけでは何も変わりません。
        </p>
      </div>

      {/* 2026-10-21 · 仮データ・不整合の点検（1行ずつ直す・元に戻せる） */}
      <DataQualityAudit />

      <div>
        <h2 className="text-base font-semibold tracking-tight text-zinc-900"
          data-testid="purchase-section-title">購入記録</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          入荷確認が書いた購入実績 {data.scanned}件 を点検しました（この画面を開いただけでは何も変わりません）。
        </p>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
        data-testid="pr-audit-readonly-note">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" />
        <span>
          下の「直す」を押した分だけ変わります。消さずに<b>取り消し</b>にするので、いつでも元に戻せます。
        </span>
      </p>

      {undoable.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2" data-testid="pr-audit-undo-list">
          <p className="text-[11px] font-semibold text-sky-800">直した分（元に戻せます）</p>
          {undoable.map((b) => (
            <div key={b.id} className="mt-1 flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[11px] text-sky-700">{b.label}</span>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => undo(b)}
                data-testid={`pr-audit-undo-${b.id}`}>
                <ArrowCounterClockwise size={12} className="mr-1" />元に戻す
              </Button>
            </div>
          ))}
        </div>
      )}

      <Section testid="pr-audit-duplicates" title="同じ入荷が二重に記録されている"
        count={c.duplicate_groups} note={`余分な行 ${c.duplicate_rows}件 — 最初の1件を残します`}>
        {data.duplicates.map((g) => (
          <div key={g.key} className="flex items-center gap-2 px-3 py-2" data-testid={`pr-audit-dup-${g.key}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-zinc-800">
                {g.request_no} / 明細{g.line_index + 1} — {g.item_name}
              </p>
              <p className="text-[11px] text-zinc-500 tabular-nums">
                残す: {g.keep.qty} × {yen(g.keep.actual_price)}（{jstDate(g.keep.received_date)}） ／
                取り消す: {g.void_count}件（
                {g.void.map((v) => `${yen(v.actual_price)} / ${jstDate(v.received_date)}`).join('・')}）
              </p>
              <p className="text-[10.5px] text-emerald-700" data-testid={`pr-audit-dup-reason-${g.key}`}>
                {g.reason}
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => apply({ kind: 'duplicate', key: g.key },
                `${g.request_no} 明細${g.line_index + 1} の重複 ${g.void_count}件 を取り消す`)}
              data-testid={`pr-audit-fix-dup-${g.key}`}>直す</Button>
          </div>
        ))}
      </Section>

      <Section testid="pr-audit-prices" title="単価が「金額 ÷ 数量」と合わない"
        count={c.unit_price_fixes} note="明細の単位で計算し直します">
        {data.unit_price_fixes.map((r) => (
          <div key={r.id} className="flex items-center gap-2 px-3 py-2" data-testid={`pr-audit-price-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-zinc-800">
                {r.request_no} / 明細{(r.line_index ?? 0) + 1} — {r.item_name}
              </p>
              <p className="text-[11px] text-zinc-500 tabular-nums">
                {r.qty} {r.uom || ''} ・{yen(r.actual_price)} ／ 単価 {yen(r.unit_price)}
                {' → '}<b className="text-emerald-700">{yen(r.expected_unit_price)}</b>
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => apply({ kind: 'unit_price', row_id: r.id },
                `${r.request_no} の単価を ${yen(r.expected_unit_price)} に直す`)}
              data-testid={`pr-audit-fix-price-${r.id}`}>直す</Button>
          </div>
        ))}
      </Section>

      <Section testid="pr-audit-junk" title="入力ミスの疑いがある行"
        count={c.junk_rows}
        note={`金額が¥10未満 ${c.junk_small_amount}件・品番が空 ${c.junk_missing_part_no}件`}>
        {data.junk_rows.slice(0, 50).map((r) => (
          <div key={r.id} className="flex items-center gap-2 px-3 py-2" data-testid={`pr-audit-junk-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-zinc-800">
                {r.request_no} / 明細{(r.line_index ?? 0) + 1} — {r.item_name}
                <span className="ml-1.5 text-[10px] text-amber-700">{r.reason}</span>
              </p>
              <p className="text-[11px] text-zinc-500 tabular-nums">
                {r.qty} ・{yen(r.actual_price)} ・単価 {yen(r.unit_price)}（{jstDate(r.received_date)}）
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => apply({ kind: 'junk', row_id: r.id },
                `${r.request_no} の行（${yen(r.actual_price)}）を取り消す`)}
              data-testid={`pr-audit-fix-junk-${r.id}`}>取り消す</Button>
          </div>
        ))}
        {c.junk_rows > 50 && (
          <p className="px-3 py-2 text-[11px] text-zinc-400">ほか {c.junk_rows - 50}件（上位50件のみ表示）</p>
        )}
      </Section>

      <Section testid="pr-audit-stock-missing" title="在庫に反映されていない入荷"
        count={c.stock_missing} note="数量を確かめてから入庫してください（数量は直せます）">
        {(data.stock_missing || []).map((r) => (
          <div key={r.key} className="flex items-center gap-2 px-3 py-2" data-testid={`pr-audit-stock-${r.key}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-zinc-800">
                {r.request_no} / 明細{r.line_index + 1} — {r.item_name}
                {r.needs_review && (
                  <span className="ml-1.5 text-[10px] text-amber-700">要確認</span>
                )}
              </p>
              <p className="text-[11px] text-zinc-500 tabular-nums">
                入荷 {r.line_qty} {r.line_uom || ''} ／ 在庫 {r.stock_before}{r.unit}
                {' → '}{Number(r.stock_before) + Number(stockQty[r.key] ?? r.proposed_qty ?? 0)}{r.unit}
                {r.pack_qty ? `（入数 ${r.pack_qty}）` : (r.needs_qty_input ? '（入数が未登録）' : '')}
              </p>
              {r.needs_review && (
                <p className="text-[10.5px] text-amber-700" data-testid={`pr-audit-stock-review-${r.key}`}>
                  在庫数を確認してから入庫してください
                </p>
              )}
            </div>
            <input type="number" min="0" className="h-8 w-24 rounded border border-zinc-300 px-2 text-[12px]"
              value={stockQty[r.key] ?? (r.proposed_qty ?? '')}
              onChange={(e) => setStockQty((q) => ({ ...q, [r.key]: e.target.value }))}
              data-testid={`pr-audit-stock-qty-${r.key}`} />
            <span className="text-[11px] text-zinc-400">{r.unit}</span>
            <Button size="sm" variant="outline"
              disabled={busy || !(Number(stockQty[r.key] ?? r.proposed_qty) > 0)}
              onClick={() => apply({ kind: 'stock_receipt', key: r.key,
                qty: Number(stockQty[r.key] ?? r.proposed_qty) },
              `${r.request_no} の ${r.item_name} を ${stockQty[r.key] ?? r.proposed_qty}${r.unit} 入庫する`)}
              data-testid={`pr-audit-fix-stock-${r.key}`}>入庫する</Button>
          </div>
        ))}
      </Section>

      <section className="rounded-xl border border-zinc-200 bg-white" data-testid="pr-audit-stock-reflected">
        <button type="button" onClick={() => setShowReflected((v) => !v)}
          className="flex w-full items-baseline gap-2 px-3 py-2 text-left"
          data-testid="pr-audit-stock-reflected-toggle">
          <h2 className="text-[13px] font-semibold text-zinc-800">旧方式で反映済み</h2>
          <span className="text-sm font-bold text-zinc-400">{c.stock_reflected_legacy}件</span>
          <span className="text-[11px] text-zinc-400">
            入荷確認が台帳の数量を直接足していた分 — 在庫は変わりません
          </span>
          <span className="ml-auto text-[11px] text-sky-700">{showReflected ? '閉じる' : '開く'}</span>
        </button>
        {showReflected && (
          <div className="divide-y divide-zinc-100 border-t border-zinc-100">
            {(data.stock_reflected_legacy || []).map((r) => (
              <div key={r.key} className="flex items-center gap-2 px-3 py-2"
                data-testid={`pr-audit-reflected-${r.key}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-medium text-zinc-800">
                    {r.request_no} / 明細{r.line_index + 1} — {r.item_name}
                  </p>
                  <p className="text-[11px] text-zinc-500 tabular-nums">
                    入荷 {r.line_qty} {r.line_uom || ''} ／ 在庫 {r.stock_before}{r.unit}（{r.evidence}）
                  </p>
                </div>
                <Button size="sm" variant="ghost" disabled={busy}
                  onClick={() => apply({ kind: 'stock_reflected', key: r.key },
                    `${r.request_no} の ${r.item_name} を「反映済み」として記録する（在庫は変わりません）`)}
                  data-testid={`pr-audit-mark-reflected-${r.key}`}>反映済みとして記録</Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <Section testid="pr-audit-unlinked" title="品目が未リンクの購入記録"
        count={c.unlinked_rows}
        note="依頼明細の品目に紐づけます（台帳の購入実績に出るようになります）">
        {(data.unlinked_rows || []).map((r) => (
          <div key={r.row_id} className="flex items-center gap-2 px-3 py-2"
            data-testid={`pr-audit-unlinked-${r.row_id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-zinc-800">
                {r.request_no} / 明細{r.line_index + 1} — {r.item_name}
              </p>
              <p className="text-[11px] text-zinc-500 tabular-nums">
                {r.qty} ・{yen(r.actual_price)}（{jstDate(r.received_date)}） ／ 紐づけ先:{' '}
                <b className="text-emerald-700">{r.part_no || r.proposed_item_name}</b>
                {r.proposed_label ? `（${r.proposed_label}）` : ''}
              </p>
              <p className="text-[10.5px] text-zinc-400">{r.reason} ／ {r.matched_by}</p>
            </div>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => apply({ kind: 'link_item', row_id: r.row_id },
                `${r.request_no} の記録を ${r.part_no || r.proposed_item_name} に紐づける`)}
              data-testid={`pr-audit-fix-unlinked-${r.row_id}`}>紐づける</Button>
          </div>
        ))}
      </Section>

      <Section testid="pr-audit-wrong-item" title="依頼明細と違う品目に紐づいている"
        count={c.wrong_item_rows} note="自動では直しません（内容確認が必要）">
        {data.wrong_item_rows.map((r) => (
          <div key={r.id} className="px-3 py-2" data-testid={`pr-audit-wrong-${r.id}`}>
            <p className="truncate text-[12px] font-medium text-zinc-800">
              <Warning size={12} className="mr-1 inline text-amber-600" />
              {r.request_no} / 明細{(r.line_index ?? 0) + 1} — {r.item_name}
            </p>
            <p className="text-[11px] text-zinc-500">
              記録の品目ID {r.item_id} ／ 依頼の品目 {r.expected_item_name || r.expected_item_id}
            </p>
          </div>
        ))}
      </Section>
    </div>
  );
}
